/**
 * «Más»: lo que no es Inicio, Comunidades ni Torneos.
 *
 * Tres cosas, y las tres se hacen poco: entrar al perfil, elegir el club que
 * abre la pestaña de Torneos, y salir de la cuenta. Ninguna merece una
 * pestaña propia, y las tres merecen estar en un lugar fijo que se encuentre
 * sin buscar — que es para lo que existe esta pantalla.
 *
 * CERRAR SESIÓN VIVE ACÁ Y NO EN LA BARRA DE ARRIBA. Estaba al lado del
 * nombre, a un toque de distancia, en todas las pantallas: es lo que menos se
 * usa de toda la aplicación y estaba en el lugar más fácil de tocar sin
 * querer.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { THEMES, type Theme } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { AccountsSection } from '../components/AccountsSection';
import { PreferencesSection } from '../components/PreferencesSection';
import { ChoiceGroup } from '../components/ChoiceGroup';
import { SignOutIcon } from '../components/icons';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../theme/ThemeProvider';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function MorePage(): JSX.Element {
  const { t } = useTranslation();
  const { session, signOut, setUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const user = session?.user;

  const [error, setError] = useState<string | null>(null);

  /**
   * EL TEMA SE APLICA ANTES DE GUARDARLO.
   *
   * `setTheme` cambia la pantalla en el acto; la llamada al servidor lo deja
   * en la cuenta para el próximo aparato. Al revés —esperar la respuesta—
   * deja medio segundo en el que se tocó «Oscuro» y no pasó nada, y en una
   * red mala ese medio segundo son tres.
   *
   * Si la llamada falla, el tema YA CAMBIÓ y se queda cambiado: el navegador
   * se lo acuerda igual. Lo que no se guardó es la cuenta, y eso es lo que
   * dice el error.
   */
  async function elegirTema(value: Theme): Promise<void> {
    setTheme(value);
    setError(null);
    try {
      await api.setTheme(value);
      if (user !== undefined) setUser({ ...user, theme: value });
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  return (
    <AppLayout>
      <h1>{t('nav.more')}</h1>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <p className="links">
        <Link className="button-like" to="/perfil">
          {t('nav.profile')}
        </Link>
        {/* CREAR UN CLUB VIVE ACÁ, y no en la lista de clubes: aquella es
            para BUSCAR uno —lo que hace casi todo el mundo casi siempre— y
            crear uno lo hace una persona una vez en la vida. Acá queda al
            lado de «Mi club», que es donde alguien va cuando piensa en su
            propio club. */}
        <Link className="button-like" to="/clubes/nuevo">
          {t('communities.create')}
        </Link>
        {/* CÓMO FUNCIONAN LOS PUNTOS, debajo de crear un club y no en una
            esquina: es lo primero que pregunta quien acaba de armar uno, y
            hasta ahora la respuesta era un PDF por WhatsApp que quedaba viejo
            el día que se tocaba una constante. Lo pidió Javier acá. */}
        <Link className="button-like" to="/puntos">
          {t('points.title')}
        </Link>
      </p>

      {/*
        ACÁ IBA «MI CLUB» Y LO SACAMOS.

        Era un club marcado que hacía que la pestaña de Torneos saltara
        derecho a él. Dos problemas: el salto se sentía como que la pestaña
        estaba rota —se tocaba «Torneos» y aparecía la pantalla de un club—, y
        el ajuste se ponía una vez y después nadie se acordaba de que existía,
        así que quien entraba a un segundo club no entendía por qué la
        pestaña seguía llevándolo al primero. Ahora Torneos siempre muestra
        los clubes y se entra al que uno quiere, que es un toque más y
        ninguna sorpresa.

        La columna sigue en la base y el servidor la sigue usando como club de
        referencia para buscar rivales; lo que se fue es la pantalla que la
        ponía y el salto que la leía.
      */}

      {/*
        CÓMO ME ENCUENTRAN PARA JUGAR: el nivel declarado, si acepto retos,
        si quiero correos y dónde estoy. Los cuatro vivían adentro del
        formulario del perfil, entre la cédula y la dirección, y Javier los
        mandó acá. Ver `PreferencesSection`.
      */}
      <PreferencesSection />

      {/* EL TEMA, ACÁ Y NO EN EL PERFIL.
          El perfil es quién sos —nombre, documento, dónde vivís—; el tema es
          un ajuste de la aplicación, como el club por defecto. Además se
          prueba mirando: se toca y la pantalla entera cambia, y para eso hay
          que estar en una pantalla con cosas, no adentro de un formulario. */}
      <section>
        <ChoiceGroup
          id="theme"
          legend={t('register.theme')}
          value={theme}
          onChange={(value) => void elegirTema(value)}
          options={THEMES.map((cual) => ({ value: cual, label: t(`theme.${cual}`) }))}
        />
      </section>

      <AccountsSection />

      {/*
        CERRAR SESIÓN: AL FINAL, CENTRADO Y CON SU ÍCONO.

        Sin título encima: un «Sesión» para una sola acción es un renglón que
        no agrega nada, y la puerta con la flecha ya dice de qué se trata.

        Y SIN SUBRAYADO, al revés que los demás enlaces de la aplicación:
        esto no lleva a ninguna parte, hace algo. El subrayado es la señal de
        «esto es un enlace» y usarlo acá la gasta.
      */}
      <p className="sign-out-row">
        <button type="button" className="link sign-out" onClick={signOut}>
          <SignOutIcon title="" size={20} />
          <span>{t('home.signOut')}</span>
        </button>
      </p>
    </AppLayout>
  );
}
